/**
 * Setelan tugas — bagian MURNI, tanpa React.
 *
 * Dua daftar yang harus ada sebelum sebuah organisasi dapat bekerja: jenis tugas (tanpanya tidak
 * ada tugas yang dapat dibuat) dan kode alasan gagal (tanpanya pengiriman gagal tidak dapat
 * ditandai). Keduanya sebelumnya hanya dapat dibuat lewat skrip provisioning.
 */

/**
 * Kategori jenis tugas — CERMIN dari katalog server di `task-type-category.ts`.
 *
 * Server yang memutuskan `requiresProof`; daftar ini hanya menerjemahkannya ke bahasa manusia.
 * Kalau keduanya menyimpang, yang terjadi bukan galat melainkan layar yang menjanjikan sifat bukti
 * yang berbeda dari yang benar-benar disimpan — dijaga uji yang MEMBACA katalog server.
 *
 * `wajibBukti` di sini SEMATA untuk keterangan di layar. Ia tidak dikirim, tidak menentukan apa
 * pun, dan tidak dipercaya server.
 */
export const KATEGORI_TUGAS = [
  {
    nilai: 'PENGIRIMAN',
    label: 'Pengiriman barang',
    keterangan: 'Barang berpindah ke penerima. Wajib foto bukti dan tanda tangan penerima.',
    wajibBukti: true,
  },
  {
    nilai: 'PENJEMPUTAN',
    label: 'Penjemputan barang',
    keterangan: 'Barang diambil dari pihak lain. Wajib foto bukti dan tanda tangan.',
    wajibBukti: true,
  },
  {
    nilai: 'KUNJUNGAN',
    label: 'Kunjungan',
    keterangan: 'Tidak ada barang berpindah, jadi tidak ada bukti serah-terima yang dapat difoto.',
    wajibBukti: false,
  },
  {
    nilai: 'DOKUMEN',
    label: 'Dokumen / tukar faktur',
    keterangan:
      'Kurir membawa berkas penagihan. Buktinya dokumen bertanda tangan, bukan foto barang.',
    wajibBukti: false,
  },
] as const;

export type NilaiKategoriTugas = (typeof KATEGORI_TUGAS)[number]['nilai'];

/** Kategori alasan gagal — cermin `CATEGORIES` di `failure-reason-code.dto.ts`. */
export const KATEGORI_ALASAN = [
  { nilai: 'recipient', label: 'Penerima' },
  { nilai: 'address', label: 'Alamat' },
  { nilai: 'goods', label: 'Barang' },
  { nilai: 'courier', label: 'Kurir' },
  { nilai: 'external', label: 'Di luar kendali' },
] as const;

export type NilaiKategoriAlasan = (typeof KATEGORI_ALASAN)[number]['nilai'];

/**
 * Kode dinormalkan, tidak ditolak.
 *
 * Orang mengetik "Kirim Barang"; server menuntut kode. Menolaknya dengan pesan galat memaksa
 * pemakai menebak bentuk yang diterima — padahal bentuknya dapat diturunkan tanpa ambiguitas.
 * Yang tidak dinormalkan adalah NAMA, karena itu tulisan yang dibaca orang.
 */
export function normalkanKode(mentah: string): string {
  return mentah
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60);
}

export interface MasalahSetelan {
  readonly bidang: string;
  readonly pesan: string;
}

export function periksaJenisTugas(input: {
  kode: string;
  nama: string;
  kategori: string;
  kodeTerpakai: readonly string[];
}): readonly MasalahSetelan[] {
  const masalah: MasalahSetelan[] = [];
  const kode = normalkanKode(input.kode);

  if (kode.length === 0) masalah.push({ bidang: 'kode', pesan: 'Kode wajib diisi.' });
  if (input.nama.trim().length === 0) masalah.push({ bidang: 'nama', pesan: 'Nama wajib diisi.' });
  if (!KATEGORI_TUGAS.some((k) => k.nilai === input.kategori)) {
    masalah.push({ bidang: 'kategori', pesan: 'Pilih kategori.' });
  }
  /*
   * Kode ganda ditolak DI SINI, bukan dibiarkan menjadi galat 500 dari batasan unik basis data.
   * Yang membacanya pemilik usaha, dan "duplicate key value violates unique constraint" bukan
   * kalimat yang dapat ia tindak lanjuti.
   */
  if (kode.length > 0 && input.kodeTerpakai.includes(kode)) {
    masalah.push({ bidang: 'kode', pesan: `Kode ${kode} sudah dipakai jenis tugas lain.` });
  }
  return masalah;
}

export function periksaAlasanGagal(input: {
  kode: string;
  label: string;
  kategori: string;
  kodeTerpakai: readonly string[];
}): readonly MasalahSetelan[] {
  const masalah: MasalahSetelan[] = [];
  const kode = normalkanKode(input.kode);

  if (kode.length === 0) masalah.push({ bidang: 'kode', pesan: 'Kode wajib diisi.' });
  if (input.label.trim().length === 0) {
    masalah.push({ bidang: 'label', pesan: 'Keterangan wajib diisi.' });
  }
  if (!KATEGORI_ALASAN.some((k) => k.nilai === input.kategori)) {
    masalah.push({ bidang: 'kategori', pesan: 'Pilih kategori.' });
  }
  if (kode.length > 0 && input.kodeTerpakai.includes(kode)) {
    masalah.push({ bidang: 'kode', pesan: `Kode ${kode} sudah dipakai alasan lain.` });
  }
  return masalah;
}
